/**
 * Per-deal checklist hook.
 *
 * Fetches:
 *   1. The `default_checklists` template for the deal's current `pipeline_id`.
 *   2. The user-edited state rows in `pipeline_property_checklists` for the
 *      same (pipeline_property_id, pipeline_id).
 *
 * Merges them into a single resolved list (`ResolvedChecklistItem[]`):
 *   - One row per default item, with the latest state attached (if any).
 *   - Plus any ad-hoc state rows where `default_checklist_id IS NULL`
 *     (rendered using their local `checklist_text` / `required`).
 *
 * Item completeness:
 *   - 'check' items: complete when `checked = true`.
 *   - 'input' items: complete when `checklist_text` is a non-empty string.
 *
 * Returns mutations to toggle / set-input on items. The hook is intentionally
 * "fat" so the UI components stay thin.
 */
import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useWorkspace } from "@/hooks/use-workspace";
import { toUserMessage } from "@/lib/supabase-errors";
import type {
  DefaultChecklistRow,
  PipelinePropertyChecklistRow,
  ResolvedChecklistItem,
} from "@/types/pipeline";

type DbResp<T> = Promise<PostgrestSingleResponse<T>>;

interface UseChecklistArgs {
  /** ID of the pipeline_properties row (the deal). */
  pipelinePropertyId: string | null | undefined;
  /** Current pipeline (stage) id — drives which default checklist applies. */
  pipelineId: string | null | undefined;
}

interface ToggleArgs {
  defaultChecklistId: string | null;
  /** Template label — used for ad-hoc rows + as the optimistic fallback. */
  label: string;
  /** Set true to mark complete, false to clear. */
  checked: boolean;
}

interface SetInputArgs {
  defaultChecklistId: string | null;
  label: string;
  /** New entered value. Empty string clears the item back to "incomplete". */
  value: string;
}

function isItemComplete(
  itemType: "check" | "input",
  state: PipelinePropertyChecklistRow | null,
): boolean {
  if (!state) return false;
  if (itemType === "input") {
    return Boolean(state.checklist_text && state.checklist_text.trim());
  }
  return state.checked === true;
}

/**
 * Pick the latest state row per (default_checklist_id) — there's no UNIQUE
 * constraint, so we always coalesce by latest `updated_at` then `created_at`.
 */
function latestByDefault(
  rows: PipelinePropertyChecklistRow[],
): Map<string | null, PipelinePropertyChecklistRow> {
  const byId = new Map<string | null, PipelinePropertyChecklistRow>();
  for (const r of rows) {
    const existing = byId.get(r.default_checklist_id);
    if (!existing) {
      byId.set(r.default_checklist_id, r);
      continue;
    }
    const aT = existing.updated_at ?? existing.created_at;
    const bT = r.updated_at ?? r.created_at;
    if (bT > aT) byId.set(r.default_checklist_id, r);
  }
  return byId;
}

export function usePipelinePropertyChecklist(args: UseChecklistArgs) {
  const { pipelinePropertyId, pipelineId } = args;
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  const enabled = Boolean(pipelinePropertyId && pipelineId);

  const queryKey = useMemo(
    () =>
      ["pipeline_property_checklist", pipelinePropertyId, pipelineId] as const,
    [pipelinePropertyId, pipelineId],
  );

  const query = useQuery({
    queryKey,
    enabled,
    queryFn: async () => {
      if (!pipelinePropertyId || !pipelineId) {
        return { defaults: [], state: [] };
      }

      const [defaultsResp, stateResp] = await Promise.all([
        (
          supabase as unknown as {
            from: (t: string) => {
              select: (s: string) => {
                eq: (
                  col: string,
                  v: string,
                ) => { order: (col: string) => DbResp<DefaultChecklistRow[]> };
              };
            };
          }
        )
          .from("default_checklists")
          .select("*")
          .eq("pipeline_id", pipelineId)
          .order("order") as DbResp<DefaultChecklistRow[]>,

        (
          supabase as unknown as {
            from: (t: string) => {
              select: (s: string) => {
                eq: (col: string, v: string) => {
                  eq: (
                    col: string,
                    v: string,
                  ) => DbResp<PipelinePropertyChecklistRow[]>;
                };
              };
            };
          }
        )
          .from("pipeline_property_checklists")
          .select("*")
          .eq("pipeline_property_id", pipelinePropertyId)
          .eq("pipeline_id", pipelineId) as DbResp<
          PipelinePropertyChecklistRow[]
        >,
      ]);

      if (defaultsResp.error) throw defaultsResp.error;
      if (stateResp.error) throw stateResp.error;
      return {
        defaults: defaultsResp.data ?? [],
        state: stateResp.data ?? [],
      };
    },
  });

  /** Merge defaults + state rows into ResolvedChecklistItem[]. */
  const items = useMemo<ResolvedChecklistItem[]>(() => {
    const defaults = query.data?.defaults ?? [];
    const state = query.data?.state ?? [];
    const stateByDefaultId = latestByDefault(state);

    const resolved: ResolvedChecklistItem[] = defaults.map((d) => {
      const s = stateByDefaultId.get(d.id) ?? null;
      return {
        key: d.id,
        label: d.checklist_text,
        itemType: d.item_type,
        required: d.required,
        complete: isItemComplete(d.item_type, s),
        defaultChecklistId: d.id,
        stateRow: s,
      };
    });

    // Append ad-hoc (default_checklist_id IS NULL) and orphaned state rows.
    for (const s of state) {
      if (s.default_checklist_id !== null) continue;
      const label = s.checklist_text ?? "(untitled)";
      resolved.push({
        key: s.id,
        label,
        itemType: "check", // ad-hoc items default to check-type
        required: s.required,
        complete: isItemComplete("check", s),
        defaultChecklistId: null,
        stateRow: s,
      });
    }
    return resolved;
  }, [query.data]);

  /**
   * Whether the gate to advance out of this pipeline is satisfied.
   * The kanban drag-drop handler reads this to block/allow moves.
   */
  const requiredUnmet = useMemo(
    () => items.filter((i) => i.required && !i.complete),
    [items],
  );

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey });
    // The kanban board derives each card's "Move to <next>" / "Move to Rehab"
    // action from the workspace-wide checklist query in usePipelineBoard
    // (`pipeline_property_checklists`, plural). Without invalidating it too,
    // completing a checklist here wouldn't surface the action button until a
    // full page reload refetched that separate query.
    void queryClient.invalidateQueries({
      queryKey: ["pipeline_property_checklists", currentWorkspace?.id],
    });
  }, [queryClient, queryKey, currentWorkspace?.id]);

  /**
   * Upsert a state row for one checklist item. Strategy:
   *   - If a state row already exists, UPDATE it.
   *   - Otherwise INSERT a new row.
   * (There's no UNIQUE constraint on (pp_id, default_id), so we manage
   * "one canonical row per default" in app code by finding the latest.)
   */
  const writeState = useCallback(
    async (
      defaultChecklistId: string | null,
      label: string,
      patch: { checked?: boolean; checklistText?: string | null },
    ) => {
      if (!pipelinePropertyId || !pipelineId || !currentWorkspace?.id) {
        throw new Error("Missing pipeline_property_id / pipeline_id / workspace");
      }

      // Find the existing canonical row for this default (or ad-hoc) item.
      const stateRows = query.data?.state ?? [];
      const matches = stateRows.filter(
        (r) => r.default_checklist_id === defaultChecklistId,
      );
      const existing =
        matches.length > 0
          ? matches.reduce((a, b) => {
              const aT = a.updated_at ?? a.created_at;
              const bT = b.updated_at ?? b.created_at;
              return bT > aT ? b : a;
            })
          : null;

      const nowCompleted =
        patch.checked === true ||
        (typeof patch.checklistText === "string" && patch.checklistText.trim() !== "");

      const completionFields = nowCompleted
        ? { completed_at: new Date().toISOString(), completed_by: user?.id ?? null }
        : { completed_at: null, completed_by: null };

      if (existing) {
        const { error } = (await (
          supabase as unknown as {
            from: (t: string) => {
              update: (v: unknown) => {
                eq: (col: string, v: string) => DbResp<unknown>;
              };
            };
          }
        )
          .from("pipeline_property_checklists")
          .update({
            ...(patch.checked !== undefined ? { checked: patch.checked } : {}),
            ...(patch.checklistText !== undefined
              ? { checklist_text: patch.checklistText }
              : {}),
            ...completionFields,
          })
          .eq("id", existing.id)) as PostgrestSingleResponse<unknown>;
        if (error) throw error;
      } else {
        const payload = {
          pipeline_property_id: pipelinePropertyId,
          pipeline_id: pipelineId,
          default_checklist_id: defaultChecklistId,
          workspace_id: currentWorkspace.id,
          // Persist the template label so the row is self-describing even for
          // default items (input editing was removed, so `patch.checklistText`
          // is undefined on a plain toggle — fall back to the label, not null).
          checklist_text:
            patch.checklistText !== undefined ? patch.checklistText : label,
          checked: patch.checked ?? false,
          required: false,
          ...completionFields,
          created_by: user?.id ?? null,
        };
        const { error } = (await (
          supabase as unknown as {
            from: (t: string) => { insert: (v: unknown) => DbResp<unknown> };
          }
        )
          .from("pipeline_property_checklists")
          .insert(payload)) as PostgrestSingleResponse<unknown>;
        if (error) throw error;
      }
    },
    [
      pipelinePropertyId,
      pipelineId,
      currentWorkspace?.id,
      user?.id,
      query.data,
    ],
  );

  const toggle = useMutation({
    mutationFn: (args: ToggleArgs) =>
      writeState(args.defaultChecklistId, args.label, { checked: args.checked }),
    onSuccess: invalidate,
    onError: (err: unknown) =>
      toast.error(toUserMessage(err, "Failed to update checklist")),
  });

  const setInput = useMutation({
    mutationFn: (args: SetInputArgs) =>
      writeState(args.defaultChecklistId, args.label, {
        checklistText: args.value,
      }),
    onSuccess: invalidate,
    onError: (err: unknown) =>
      toast.error(toUserMessage(err, "Failed to save value")),
  });

  return {
    items,
    requiredUnmet,
    isLoading: query.isLoading,
    error: query.error,
    toggle,
    setInput,
    refetch: query.refetch,
  };
}
